// frontend/src/components/workflows/types.ts - FIXED StageRole references
import type { 
  Group, 
  Organization, 
  WorkflowStage as APIWorkflowStage,
  Workflow as APIWorkflow,
  StageRole,
  Priority
} from '@/api/types';

// Re-export API types using 'export type' for isolatedModules compatibility
export type { Group, Organization, StageRole } from '@/api/types';
export type WorkflowPriority = Priority; // Use Priority from API types

// UPDATED: Extended WorkflowStage interface that matches backend structure
export interface WorkflowStage extends Omit<APIWorkflowStage, 'order'> {
  // Core backend fields (inherited from APIWorkflowStage)
  _id?: string;
  stageName: string; // Backend uses stageName, not name
  stageIndex: number;
  role: StageRole;
  description?: string; // FIXED: Make description optional but available
  mandatory?: boolean;
  approvalGroups?: string[]; // Backend uses approvalGroups
  timeLimit?: { hours: number };
  allowParallel?: boolean;
  autoAdvance?: boolean;
  requiredApprovals?: number;
  stats?: {
    responsesProcessed: number;
    avgProcessingTime: number;
    timeoutCount: number;
  };
  createdAt?: string;
  updatedAt?: string;
  
  // Frontend-specific fields for display and editing
  order: number; // For frontend display ordering
}

// UPDATED: Extended Workflow interface with frontend enhancements
export interface Workflow extends APIWorkflow {
  stages: WorkflowStage[]; // Use our extended WorkflowStage type
}

// Component prop interfaces with proper typing
export interface StageEditorProps {
  stages: WorkflowStage[];
  availableGroups: Group[]; // Use API Group type directly
  onStageUpdate: (index: number, updatedStage: Partial<WorkflowStage>) => void;
  onStageAdd: () => void;
  onStageRemove: (index: number) => void;
  onStageMove: (fromIndex: number, toIndex: number) => void;
  errors?: { [index: number]: { [field: string]: string } }; // No undefined values
}

// UPDATED: Stage request interfaces for backend API
export interface WorkflowStageCreateRequest {
  stageName: string;
  stageIndex: number;
  role: StageRole;
  description?: string;
  mandatory?: boolean;
  approvalGroups?: string[];
  timeLimit?: { hours: number };
  allowParallel?: boolean;
  autoAdvance?: boolean;
  requiredApprovals?: number;
}

export interface WorkflowStageUpdateRequest extends WorkflowStageCreateRequest {
  _id?: string;
}

// UPDATED: Create workflow request with proper backend structure
export interface CreateWorkflowRequest {
  name: string;
  description?: string;
  stages: WorkflowStageCreateRequest[];
  isActive?: boolean;
  priority?: WorkflowPriority;
  isTemplate?: boolean;
  templateCategory?: 'standard' | 'expedited' | 'review-heavy' | 'simple' | 'custom';
  constraints?: WorkflowConstraints;
  organizationId?: string; // For implementors creating in specific org
}

// UPDATED: Update workflow request with proper backend structure
export interface UpdateWorkflowRequest {
  name?: string;
  description?: string;
  stages?: WorkflowStageUpdateRequest[];
  isActive?: boolean;
  priority?: WorkflowPriority;
  isTemplate?: boolean;
  templateCategory?: 'standard' | 'expedited' | 'review-heavy' | 'simple' | 'custom';
  constraints?: WorkflowConstraints;
}

// Workflow constraints interface - aligned with API types
export interface WorkflowConstraints {
  maxParallelResponses?: number; // Optional to match API
  globalTimeLimit?: number;
  allowedDepartments?: string[]; // Optional to match API
  minimumUserRole?: 'contributor' | 'admin' | 'implementor'; // Optional to match API
}

// Form and validation interfaces
export interface WorkflowFormData {
  name: string;
  description: string;
  priority: WorkflowPriority;
  isActive: boolean;
  isTemplate: boolean;
  templateCategory?: 'standard' | 'expedited' | 'review-heavy' | 'simple' | 'custom';
  stages: WorkflowStage[];
  constraints: WorkflowConstraints;
}

export interface WorkflowFormErrors {
  [field: string]: string;
}

export interface WorkflowValidationErrors {
  formErrors: WorkflowFormErrors;
  stageErrors: { [index: number]: { [field: string]: string } };
}

// Utility class for workflow operations
export class WorkflowUtils {
  // FIXED: Get stage role color for UI display - using correct StageRole values
  static getStageRoleColor(role: StageRole): string {
    switch (role) {
      case 'Approver':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'Editor':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'Sender':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
      case 'Commenter':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  }

  // Get priority color for UI display
  static getPriorityColor(priority: WorkflowPriority): string {
    switch (priority) {
      case 'urgent':
        return 'text-red-600 dark:text-red-400';
      case 'high':
        return 'text-orange-600 dark:text-orange-400';
      case 'medium':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'low':
        return 'text-green-600 dark:text-green-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  }

  // Get priority badge color
  static getPriorityBadgeColor(priority: WorkflowPriority): string {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'low':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  }

  // Validate workflow form data
  static validateWorkflow(data: WorkflowFormData): WorkflowValidationErrors {
    const formErrors: WorkflowFormErrors = {};
    const stageErrors: { [index: number]: { [field: string]: string } } = {};

    // Basic validation
    if (!data.name?.trim()) {
      formErrors.name = 'Workflow name is required';
    } else if (data.name.length < 3) {
      formErrors.name = 'Workflow name must be at least 3 characters';
    } else if (data.name.length > 100) {
      formErrors.name = 'Workflow name must be less than 100 characters';
    }

    if (data.description && data.description.length > 500) {
      formErrors.description = 'Description must be less than 500 characters';
    }

    // Stage validation
    if (data.stages.length === 0) {
      formErrors.stages = 'At least one stage is required';
    }

    data.stages.forEach((stage, index) => {
      const stageErrs: { [field: string]: string } = {};

      if (!stage.stageName?.trim()) {
        stageErrs.stageName = 'Stage name is required';
      } else if (stage.stageName.length > 50) {
        stageErrs.stageName = 'Stage name must be less than 50 characters';
      }

      if (!stage.role) {
        stageErrs.role = 'Stage role is required';
      }

      if (stage.description && stage.description.length > 200) {
        stageErrs.description = 'Stage description must be less than 200 characters';
      }

      if (stage.timeLimit && stage.timeLimit.hours < 1) {
        stageErrs.timeLimit = 'Time limit must be at least 1 hour';
      }

      if (stage.requiredApprovals && stage.requiredApprovals < 1) {
        stageErrs.requiredApprovals = 'Required approvals must be at least 1';
      }

      if (Object.keys(stageErrs).length > 0) {
        stageErrors[index] = stageErrs;
      }
    });

    // Check for duplicate stage names
    const stageNames = data.stages.map(s => s.stageName?.toLowerCase().trim()).filter(Boolean);
    const duplicateNames = stageNames.filter((name, index) => stageNames.indexOf(name) !== index);
    if (duplicateNames.length > 0) {
      formErrors.stages = 'Stage names must be unique';
    }

    // Constraints validation
    if (data.constraints.maxParallelResponses && (data.constraints.maxParallelResponses < 1 || data.constraints.maxParallelResponses > 1000)) {
      formErrors.maxParallelResponses = 'Max parallel responses must be between 1 and 1000';
    }

    if (data.constraints.globalTimeLimit && data.constraints.globalTimeLimit < 1) {
      formErrors.globalTimeLimit = 'Global time limit must be at least 1 hour';
    }

    return { formErrors, stageErrors };
  }

  // Create a new stage with defaults
  static createNewStage(order: number, availableGroups: Group[]): WorkflowStage {
    return {
      stageName: `Stage ${order + 1}`,
      stageIndex: order,
      role: 'Approver' as StageRole,
      description: '', // Ensure description field exists
      mandatory: true,
      approvalGroups: availableGroups.length > 0 ? [availableGroups[0]._id || availableGroups[0].id || ''] : [],
      timeLimit: { hours: 24 },
      allowParallel: false,
      autoAdvance: false,
      requiredApprovals: 1,
      order // Frontend display field
    };
  }

  // Calculate workflow metrics
  static calculateMetrics(workflow: Workflow) {
    const stages = workflow.stages || [];
    return {
      totalStages: stages.length,
      mandatoryStages: stages.filter(s => s.mandatory).length,
      approverStages: stages.filter(s => s.role === 'Approver').length,
      editorStages: stages.filter(s => s.role === 'Editor').length,
      senderStages: stages.filter(s => s.role === 'Sender').length,
      commenterStages: stages.filter(s => s.role === 'Commenter').length, // FIXED: Changed from reviewerStages
      hasTimeLimit: stages.some(s => s.timeLimit),
      estimatedTimeHours: stages.reduce((total, s) => total + (s.timeLimit?.hours || 24), 0),
      parallelStages: stages.filter(s => s.allowParallel).length,
      autoAdvanceStages: stages.filter(s => s.autoAdvance).length
    };
  }

  // Format time duration for display
  static formatDuration(minutes: number): string {
    if (minutes < 60) return `${Math.round(minutes)}m`;
    if (minutes < 1440) return `${Math.round(minutes / 60)}h`;
    return `${Math.round(minutes / 1440)}d`;
  }

  // Get workflow status color
  static getWorkflowStatusColor(isActive: boolean): string {
    return isActive 
      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
      : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
  }
}